// deps
import cx from "classnames";
import React, { memo, ReactElement, useMemo, useCallback, useEffect } from "react";

// dls
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { default as ChevronDownIcon } from "@swiggy-private/dls-web-icons/dist/web/generic/chevronDown/12";

// internal deps
import { PAYMENT_CODES } from "@swiggy-private/payment-types";
import { Tick } from "@swiggy-private/core-ui";

// interfaces
import {
    SwiggyMoneyActions,
    SplitPayData,
    SPLITPAY_SELECTION_TYPE,
    SplitpayAnalyticsEvents,
} from "@payment-ui/interfaces";

// widgets
import { SwiggyMoneyInfo, PaymentMethodIcon, PaymentMethodName, ToggleSwitch } from "@payment-ui/widgets/index";
import { FlipkartSuperCoinTooltip } from "@payment-ui/widgets/flipkart-super-coin-tooltip";

//components
import AddMoneyButton from "./components/AddMoneyButton";
import ShowMessages from "./components/ShowMessages";

// helpers
import {
    getSplitpayClickEventContext,
    getSplitPayDisplayName,
    getSplitpayHelperMeta,
    getSplitPayIconUrl,
    getSplitpayIdentifier,
} from "@payment-ui/helpers/SplitPay";
import { isEmpty } from "@swiggy-private/js-utils";
import { getCloudinaryUrl } from "@swiggy-private/core-ui";

// constants
import { ACCESSIBILITY_ROLES, TEXTS } from "@payment-ui/constants/index";
import { SPLIT_PAY_BANNER_MAIN_ID } from "@payment-ui/constants/SplitPayBanner";

// test-ids
import { getSplitPayBannerTestIds } from "@swiggy-private/payment-test-ids";

//styles
import styles from "./SplitPayBanner.scss";
import { Icon, IconWrapper, ViewAllRewardsContainer } from "./SplitpayBanner.styles";
import { OpacityWrapper } from "./styles";

interface SplitPayBannerProps {
    data: SplitPayData;
    handleChange: (isSplitPayChecked: boolean, methodData: SplitPayData, removeReward?: boolean) => void;
    isSplitPayChecked: boolean;
    className?: string;
    isFrictionLayout?: boolean;
    paymentGroupBanner?: ReactElement | null;
    addMoneyActions?: SwiggyMoneyActions["addMoneyActions"];
    isAddMoneyCtaDisabled?: boolean;
    swiggyMoneyInfoActions?: SwiggyMoneyActions["swiggyMoneyInfoActions"];
    onFlipkartToolTipShow?: () => void;
    showViewAllRewardsCta?: boolean;
    handleViewAllRewardsClick?: (show: boolean) => void;
    selectionType?: SPLITPAY_SELECTION_TYPE;
    analyticsEvents?: SplitpayAnalyticsEvents;
}

function SplitPayBanner(props: SplitPayBannerProps): ReactElement {
    const {
        data,
        handleChange,
        isSplitPayChecked,
        className,
        addMoneyActions,
        isFrictionLayout = false,
        paymentGroupBanner,
        isAddMoneyCtaDisabled,
        swiggyMoneyInfoActions,
        onFlipkartToolTipShow,
        showViewAllRewardsCta = false,
        handleViewAllRewardsClick,
        selectionType = SPLITPAY_SELECTION_TYPE.TOGGLE,
        analyticsEvents,
    } = props;

    /* Disabling Add Money CTA on Friction Layout Pages as it is has redirection issues and not required for now */
    const canShowAddMoneyCta = !isFrictionLayout && data?.showAddMoneyCta && data?.addMoneyCtaMessage != null;

    const infoMessage = canShowAddMoneyCta ? data?.meta?.infoMessageWithCta : data?.meta?.infoMessageWithoutCta;

    const canShowInfoMessage = useMemo(
        () => !!infoMessage || !!data.meta.learnMoreHalfCard,
        [infoMessage, data.meta.learnMoreHalfCard],
    );

    const splitPayTestIds = useMemo(() => getSplitPayBannerTestIds(getSplitpayIdentifier(data)), [data]);

    const { isEnabled, poweredByLogo, showBannerMessage, showDisableMessage, showOfferMessage, showPromoMessage } =
        useMemo(() => getSplitpayHelperMeta(data), [data]);

    const isFlipkartSuperCoinMethod = data?.paymentCode === PAYMENT_CODES.FLIPKART_SUPER_COIN;
    const hasCustomLogo = isFlipkartSuperCoinMethod || data.isReward;
    const disabledCustomLogo = hasCustomLogo && !isEnabled;

    const splitPayIcon = useMemo(
        (): ReactElement => (
            <PaymentMethodIcon
                src={getSplitPayIconUrl(data.meta, disabledCustomLogo)}
                imgClassName={hasCustomLogo ? "" : styles.splitPayMainIcon}
                alt={data.displayName}
                data-testid={splitPayTestIds.MAIN_ICON}
                poweredByIconAlt={data.poweredBy ?? ""}
                customLogo={!hasCustomLogo}
                poweredByLogo={
                    isEmpty(poweredByLogo) ? "" : getCloudinaryUrl(poweredByLogo, { grayscale: disabledCustomLogo })
                }
            />
        ),
        [
            data.meta,
            hasCustomLogo,
            splitPayTestIds.MAIN_ICON,
            disabledCustomLogo,
            poweredByLogo,
            data.poweredBy,
            data.displayName,
        ],
    );

    const infoMessageAndPayButtonClass = cx({
        [styles.splitPayInfoMessageAndButtonWithAddMoneyCta]: canShowAddMoneyCta,
        [styles.splitPayInfoMessageAndButtonWithoutAddMoneyCta]: !canShowAddMoneyCta,
    });

    const toggleClass = cx(styles.splitPayToggle, {
        [styles.splitPayToggleChecked]: isSplitPayChecked,
    });

    const splitPayClass = cx(styles.splitPayContainer, className, {
        [styles.splitPayContainerDisabled]: !isEnabled,
    });

    const methodTitleClass = cx(styles.splitPayMainTitle, {
        [styles.splitPayMainTitleDisabled]: !isEnabled,
    });

    const infoMessageContainer = useMemo(() => {
        return (
            canShowInfoMessage &&
            isSplitPayChecked && (
                <div>
                    <SwiggyMoneyInfo
                        swiggyMoneyInfoHalfCardDetails={data.meta.learnMoreHalfCard}
                        canShowSwiggyMoneyInfoCta={data.showLearnMoreHalfCard}
                        info={infoMessage}
                        infoClassName={styles.splitPayInfoMessage}
                        swiggyMoneyInfoCtaClassName={styles.splitPayLearnMoreCta}
                        actions={swiggyMoneyInfoActions}
                    />
                </div>
            )
        );
    }, [
        canShowInfoMessage,
        isSplitPayChecked,
        data.meta.learnMoreHalfCard,
        data.showLearnMoreHalfCard,
        infoMessage,
        swiggyMoneyInfoActions,
    ]);

    const selectionComponent = useMemo(() => {
        if (!isEnabled) {
            return null;
        }
        if (selectionType === SPLITPAY_SELECTION_TYPE.TOGGLE) {
            return (
                <ToggleSwitch
                    data-testid={`${splitPayTestIds.SELECTION_COMP}__toggle`}
                    selected={props.isSplitPayChecked}
                    className={toggleClass}
                    isDisabled={data?.upiAppUnavailable}
                />
            );
        } else {
            return <Tick data-testid={`${splitPayTestIds.SELECTION_COMP}__tick`} selected={props.isSplitPayChecked} />;
        }
    }, [
        props.isSplitPayChecked,
        toggleClass,
        selectionType,
        isEnabled,
        splitPayTestIds.SELECTION_COMP,
        data?.upiAppUnavailable,
    ]);

    const showInfoMessageOrAddMoneyCTA = canShowAddMoneyCta || infoMessageContainer;

    const splitPayDisplayName = useMemo(() => getSplitPayDisplayName(data), [data]);

    const promoMessage = showPromoMessage ? data?.meta?.promoMsg : "";

    const addMoneyButtonFocus = useMemo(() => Number(data?.eligibleBalance) !== 0, [data?.eligibleBalance]);

    const toggleSplitPayEvent = useCallback(() => {
        if (selectionType === SPLITPAY_SELECTION_TYPE.TOGGLE && !isEmpty(analyticsEvents?.splitpayClickEvent)) {
            analyticsEvents?.splitpayClickEvent(getSplitpayClickEventContext(data, !isSplitPayChecked));
        } else if (
            selectionType === SPLITPAY_SELECTION_TYPE.TICK &&
            !isEmpty(analyticsEvents?.rewardsInHalfcardClickEvent)
        ) {
            analyticsEvents?.rewardsInHalfcardClickEvent(data.displayName, !isSplitPayChecked);
        }
    }, [selectionType, analyticsEvents, data, isSplitPayChecked]);

    const toggleSplitPay = useCallback(() => {
        toggleSplitPayEvent();

        if (isEnabled && !data?.upiAppUnavailable) {
            handleChange(!isSplitPayChecked, data, true);
        }
    }, [handleChange, isSplitPayChecked, data, isEnabled, toggleSplitPayEvent]);

    const _handleViewAllRewardsClick = useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            event.stopPropagation();
            if (!isEmpty(analyticsEvents?.viewAllRewardsCtaClickEvent)) {
                analyticsEvents?.viewAllRewardsCtaClickEvent();
            }
            handleViewAllRewardsClick && handleViewAllRewardsClick(true);
        },
        [handleViewAllRewardsClick, analyticsEvents],
    );

    useEffect(() => {
        if (!isEmpty(analyticsEvents?.viewAllRewardsCtaImpressionEvent) && showViewAllRewardsCta) {
            analyticsEvents?.viewAllRewardsCtaImpressionEvent();
        }
    }, [showViewAllRewardsCta, analyticsEvents]);
    return (
        <div className={styles.splitPay} data-testid={splitPayTestIds.CONTAINER}>
            <div className={splitPayClass}>
                {paymentGroupBanner}
                <div
                    onClick={toggleSplitPay}
                    className={styles.splitPayMain}
                    data-testid={splitPayTestIds.MAIN}
                    role={ACCESSIBILITY_ROLES.CHECKBOX}
                    aria-checked={isSplitPayChecked}
                    aria-labelledby={SPLIT_PAY_BANNER_MAIN_ID}
                    tabIndex={0}
                >
                    <div className={styles.splitPayMainContainer}>
                        <OpacityWrapper disabled={!!data?.upiAppUnavailable}>{splitPayIcon}</OpacityWrapper>
                        <div className={styles.splitPayMainContainerContent}>
                            <OpacityWrapper
                                disabled={!!data?.upiAppUnavailable}
                                className={styles.splitPayTitleBalanceContainer}
                            >
                                <PaymentMethodName
                                    data-testid={splitPayTestIds.MAIN_TITLE}
                                    displayName={splitPayDisplayName}
                                    enabled={isEnabled}
                                    promoMsg={promoMessage}
                                    className={methodTitleClass}
                                />

                                {isEnabled && <p className={styles.splitPayMainBalance}>{data.eligibleBalance} </p>}
                                {selectionComponent}
                            </OpacityWrapper>
                            <OpacityWrapper disabled={!!data?.upiAppUnavailable}>
                                <ShowMessages
                                    showOfferMessage={showOfferMessage}
                                    showBannerMessage={showBannerMessage}
                                    showDisableMessage={showDisableMessage}
                                    offerMessage={data?.meta?.offerMessage}
                                    bannerMessage={data?.meta?.bannerMessage}
                                    disableMessage={data?.meta?.disableMessage}
                                />
                            </OpacityWrapper>
                            {showViewAllRewardsCta && (
                                <ViewAllRewardsContainer onClick={_handleViewAllRewardsClick}>
                                    <Text
                                        color="text_Button_Secondary"
                                        variant="V2.body3.neutral.bold"
                                        dangerouslySetInnerHTML={{ __html: TEXTS.VIEW_ALL_REWARDS }}
                                    />
                                    <IconWrapper>
                                        <Icon Icon={ChevronDownIcon} fillColor="text_Button_Secondary" />
                                    </IconWrapper>
                                </ViewAllRewardsContainer>
                            )}

                            {isSplitPayChecked && showInfoMessageOrAddMoneyCTA && (
                                <div className={infoMessageAndPayButtonClass}>
                                    {canShowAddMoneyCta && !isEmpty(addMoneyActions) && (
                                        <AddMoneyButton
                                            onClick={addMoneyActions?.onAddMoneyClick}
                                            disabled={!!isAddMoneyCtaDisabled}
                                            onView={addMoneyActions?.onAddMoneyView}
                                            ctaMessage={data.addMoneyCtaMessage}
                                            focusOnMount={addMoneyButtonFocus}
                                            testId={splitPayTestIds.ADD_MONEY_CTA}
                                        />
                                    )}
                                    {infoMessageContainer}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {isFlipkartSuperCoinMethod && isEnabled && (
                <FlipkartSuperCoinTooltip onFlipkartToolTipShow={onFlipkartToolTipShow} />
            )}
        </div>
    );
}

export default memo(SplitPayBanner);
